// ============================================
// src/PixelTracking.js
// Complete Pixel Tracking System
// Facebook, Google GA4, TikTok
// ============================================

const PIXEL_CONFIG = {
  facebook: {
    pixelId: '1314355800215745',
    enabled: true
  },
  google: {
    measurementId: 'G-7FSVTZEZGZ',
    adsConversionId: null, // Add later when you create Google Ads account
    enabled: true
  },
  tiktok: {
    pixelId: 'D8GGAO3C77UA4F3IKGG0',
    enabled: true
  }
};

// ============================================
// INITIALIZE ALL PIXELS
// ============================================
export const initializeAllPixels = () => {
  initFacebookPixel();
  initGoogleAnalytics();
  initTikTokPixel();
  console.log('✅ All pixels initialized');
};

// ============================================
// FACEBOOK META PIXEL
// ============================================
const initFacebookPixel = () => {
  if (!PIXEL_CONFIG.facebook.enabled) return;
  const pixelId = PIXEL_CONFIG.facebook.pixelId;

  // eslint-disable-next-line
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  console.log('✅ Facebook Pixel initialized:', pixelId);
};

// ============================================
// GOOGLE ANALYTICS 4
// ============================================
const initGoogleAnalytics = () => {
  if (!PIXEL_CONFIG.google.enabled) return;
  const { measurementId, adsConversionId } = PIXEL_CONFIG.google;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId);

  if (adsConversionId) {
    gtag('config', adsConversionId);
  }

  console.log('✅ Google Analytics initialized:', measurementId);
};

// ============================================
// TIKTOK PIXEL
// ============================================
const initTikTokPixel = () => {
  if (!PIXEL_CONFIG.tiktok.enabled) return;
  const pixelId = PIXEL_CONFIG.tiktok.pixelId;

  // eslint-disable-next-line
  !function (w, d, t) {
    w.TiktokAnalyticsObject=t;
    var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){
      var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;
      ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;
      ttq._t=ttq._t||{};ttq._t[e]=+new Date;
      ttq._o=ttq._o||{};ttq._o[e]=n||{};
      n=document.createElement("script");
      n.type="text/javascript";n.async=!0;
      n.src=r+"?sdkid="+e+"&lib="+t;
      e=document.getElementsByTagName("script")[0];
      e.parentNode.insertBefore(n,e)
    };
    ttq.load(pixelId);
    ttq.page();
  }(window, document, 'ttq');

  console.log('✅ TikTok Pixel initialized:', pixelId);
};

// ============================================
// TRACK EVENTS
// ============================================

// 📄 PAGE VIEW
export const trackPageView = (pageName) => {
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'PageView');
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'page_view', { page_title: pageName });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.page();
  }
};

// 👤 SIGN UP
export const trackSignUp = (method = 'email') => {
  console.log('📊 Tracking: SignUp');
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'CompleteRegistration', { method });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'sign_up', { method });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('CompleteRegistration');
  }
};

// 🔐 LOGIN
export const trackLogin = (method = 'email') => {
  console.log('📊 Tracking: Login');
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'login', { method });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('Login');
  }
};

// 📚 BOOK CLAIMED
export const trackBookClaimed = (bookTitle, coinsRequired) => {
  console.log('📊 Tracking: BookClaimed -', bookTitle);
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: bookTitle,
      content_category: 'BookClaim',
      value: coinsRequired,
      currency: 'USD'
    });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'book_claimed', {
      book_title: bookTitle,
      coins_required: coinsRequired
    });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('AddToWishlist', { content_name: bookTitle });
  }
};

// 📝 REVIEW SUBMITTED
export const trackReviewSubmitted = (bookTitle, rating) => {
  console.log('📊 Tracking: ReviewSubmitted -', bookTitle);
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'SubmitApplication', {
      content_name: bookTitle,
      content_category: 'Review'
    });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'review_submitted', { book_title: bookTitle, rating });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('SubmitForm', { content_name: bookTitle });
  }
};

// 📖 BOOK UPLOADED
export const trackBookUploaded = (bookTitle, genre) => {
  console.log('📊 Tracking: BookUploaded -', bookTitle);
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'CustomizeProduct', {
      content_name: bookTitle,
      content_category: genre
    });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'book_uploaded', { book_title: bookTitle, genre });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('AddToCart', { content_name: bookTitle });
  }
};

// 💳 SUBSCRIPTION PURCHASED
export const trackSubscriptionPurchase = (planName, amount) => {
  console.log('📊 Tracking: Purchase -', planName, '$' + amount);
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'Purchase', {
      value: amount,
      currency: 'USD',
      content_name: planName,
      content_type: 'subscription'
    });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: Date.now().toString(),
      value: amount,
      currency: 'USD',
      items: [{ item_name: planName, price: amount }]
    });
    if (PIXEL_CONFIG.google.adsConversionId) {
      window.gtag('event', 'conversion', {
        send_to: `${PIXEL_CONFIG.google.adsConversionId}/purchase`,
        value: amount,
        currency: 'USD'
      });
    }
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('PlaceAnOrder', {
      value: amount,
      currency: 'USD',
      content_name: planName
    });
  }
};

// 👁️ VIEW CONTENT
export const trackViewContent = (pageName) => {
  if (PIXEL_CONFIG.facebook.enabled && window.fbq) {
    window.fbq('track', 'ViewContent', { content_name: pageName });
  }
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'view_item', { item_name: pageName });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('ViewContent', { content_name: pageName });
  }
};

// 🔗 AFFILIATE CLICK
export const trackAffiliateClick = (referralCode) => {
  if (PIXEL_CONFIG.google.enabled && window.gtag) {
    window.gtag('event', 'affiliate_click', { referral_code: referralCode });
  }
  if (PIXEL_CONFIG.tiktok.enabled && window.ttq) {
    window.ttq.track('ClickButton', { content_name: 'affiliate_' + referralCode });
  }
};

export default PIXEL_CONFIG;